import React from 'react'
import { ClockCircleOutlined, TagOutlined } from '@ant-design/icons'

import { AbsorptionType } from '../../../api/reconciliation-v2-enums'
import type {
  AllocationTicket,
  TransactionViewAllocation,
  TransactionViewRow,
} from '../../../api/transaction-details-api-client'
import {
  canReturnRow,
  collectAllocationTickets,
  DASH,
  DisplayRow,
  formatDate,
  formatMoney,
  formatMonth,
  getHighlightSegments,
  GRID_TEMPLATE_COLUMNS,
  insuredPersonName,
  reconStatusLabel,
  rowNote,
  ReconStatusLabel,
  rowActionLabel,
  sourceLabel,
  summarizeAllocationField,
  truncateMiddle,
} from '../helpers'
import { useCanWriteReconciliation } from '../../ReconciliationV2/useCanWriteReconciliation'
import styles from './TransactionsTable.module.scss'

/**
 * One transaction row: a plain row (0 or 1 allocations) or, for a split
 * transaction (>1 allocation), a "Split N" parent row followed by one
 * indented child row per allocation — see buildDisplayRows in helpers.ts.
 */

interface TransactionRowProps {
  row: DisplayRow
  searchQuery: string
  onOpen: (txn: TransactionViewRow) => void
  /** Opens the tickets drawer with the same list the badge counts. */
  onOpenTickets: (tickets: AllocationTicket[]) => void
  /**
   * Jumps to the Benefits tab and opens the Adjust Payment modal on this
   * allocation's (benefit, coverage-month) cell, preselecting `transactionId`'s
   * card. Only wired on rows with a single allocation (plain + child rows) —
   * see the Benefit ID cell below.
   */
  onOpenAdjust: (
    allocation: TransactionViewAllocation,
    transactionId: string
  ) => void
  /**
   * Opens the Adjust Payment modal in place for this row's action: allocate an
   * unallocated transaction (unassigned mode) or edit an allocated one (cell
   * mode). The parent (TransactionDetailsPanel) builds the right target.
   */
  onRowAction: (row: DisplayRow) => void
  /** Opens the event-history drawer for this transaction. */
  onOpenHistory: (txn: TransactionViewRow) => void
  /** Opens the Adjust Payment modal on its Return section for this row. */
  onReturn: (row: DisplayRow) => void
  /** Confirms and un-returns this transaction. */
  onUnreturn: (txn: TransactionViewRow) => void
}

const reconStatusClassName = (recon: ReconStatusLabel): string => {
  switch (recon) {
    case ReconStatusLabel.Returned:
      return styles.returned
    case ReconStatusLabel.Refund:
    case ReconStatusLabel.Absorbed:
      return styles.refund
    case ReconStatusLabel.Reconciled:
      return styles.reconciled
    case ReconStatusLabel.Pending:
      return styles.pending
  }
}

const Highlighted: React.FC<{ text: string; query: string }> = ({
  text,
  query,
}) => (
  <>
    {getHighlightSegments(text, query).map((seg, i) =>
      seg.matched ? (
        <mark key={i} className={styles.highlight}>
          {seg.text}
        </mark>
      ) : (
        <React.Fragment key={i}>{seg.text}</React.Fragment>
      )
    )}
  </>
)

const TransactionRow: React.FC<TransactionRowProps> = ({
  row,
  searchQuery,
  onOpen,
  onOpenTickets,
  onOpenAdjust,
  onRowAction,
  onOpenHistory,
  onReturn,
  onUnreturn,
}) => {
  const { kind, txn, allocation } = row
  // Write gate — mirrors the EPS AbilityGuard(Write, PaymentReconciliation) on
  // the allocate/edit endpoints this row's action opens the modal to call.
  const { allowed: canWrite } = useCanWriteReconciliation()
  const isParent = kind === 'parent'
  const isChild = kind === 'child'

  // On an unallocated (plain) row the benefit columns fall back to the resolved
  // benefit (when the backend could determine one) so a not-yet-allocated
  // transaction still shows Carrier/Benefit ID/Company/Insured — everything but
  // Month, which has no coverage period until it's allocated.
  const resolved = allocation ? null : txn.resolvedBenefit
  const carrier = isParent
    ? summarizeAllocationField(txn.allocations, (a) => a.carrierName)
    : (allocation?.carrierName ?? resolved?.carrierName ?? DASH)
  const benefitId = isParent
    ? DASH
    : (allocation?.benefitId ?? resolved?.benefitId ?? DASH)
  const companyEeId = isParent
    ? DASH
    : (allocation?.employeePublicKey ?? resolved?.employeePublicKey ?? DASH)
  const companyName = isParent
    ? summarizeAllocationField(txn.allocations, (a) => a.companyName)
    : (allocation?.companyName ?? resolved?.companyName ?? DASH)
  const insuredName = isParent
    ? `Split ${txn.allocations.length}`
    : allocation
      ? insuredPersonName(allocation)
      : resolved
        ? insuredPersonName(resolved)
        : DASH
  const month = allocation ? formatMonth(allocation.periodStartDate) : DASH
  const amount =
    isChild && allocation
      ? formatMoney(Number(allocation.amount))
      : formatMoney(txn.amount)
  const noteText = rowNote(txn, allocation, isParent)
  const recon = reconStatusLabel(txn)
  const reconClassName = reconStatusClassName(recon)
  const tickets = isParent
    ? collectAllocationTickets(txn.allocations)
    : (allocation?.tickets ?? [])

  // Absorption sub-row: an indented write-off line beneath the transaction, like
  // a split child but money we absorbed rather than allocated. Absorptions carry
  // no benefit/period now, so those columns render a dash.
  if (kind === 'absorption' && row.absorption) {
    const absorption = row.absorption
    const absorptionTypeLabel =
      absorption.absorptionType === AbsorptionType.Full ? 'Full' : 'Partial'
    return (
      <div
        className={`${styles.row} ${styles.childRow}`}
        style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
        onClick={() => onOpen(txn)}
      >
        {/* Column order must match GRID_TEMPLATE_COLUMNS / TableHead:
            TxNumber, TxId, PaymentType, Amount, Date, Carrier, BenefitId,
            CompanyEeId, CompanyName, Insured, Month, Recon, Note, Ticket,
            Action, Log. */}
        <span className={`${styles.cell} ${styles.indent}`}>↳</span>
        <div className={styles.cellStack} />
        <span className={styles.cell}>
          <span className={styles.paymentBadge}>Absorbed</span>
        </span>
        <span className={`${styles.cell} ${styles.num}`}>
          {`−${formatMoney(absorption.amount)}`}
        </span>
        <span className={styles.cell}>{formatDate(absorption.createdAt)}</span>
        <span className={styles.cell}>{DASH}</span>
        <span className={styles.cell}>{DASH}</span>
        <span className={styles.cell}>{DASH}</span>
        <span className={styles.cell}>{DASH}</span>
        <span className={styles.cell}>
          <span
            className={styles.splitBadge}
          >{`Absorbed · ${absorptionTypeLabel}`}</span>
        </span>
        <span className={styles.cell}>{DASH}</span>
        <span className={`${styles.cell} ${styles.refund}`}>Absorbed</span>
        {/* Note: the write-off justification, restated on every absorb submit.
            Null only for absorptions recorded before the note existed. The
            PARENT row's Note cell stays an ALLOCATION summary — folding
            absorption notes in would blur two grains in one cell. */}
        <span
          className={`${styles.cell}${absorption.note ? '' : ` ${styles.muted}`}`}
          title={absorption.note ?? undefined}
        >
          {absorption.note ?? DASH}
        </span>
        <span className={styles.cell}>{DASH}</span>
        {/* Action + Log columns — no per-row action on an absorption sub-row. */}
        <span className={styles.cell} />
        <span className={styles.cell} />
      </div>
    )
  }

  return (
    <div
      className={`${styles.row}${isChild ? ` ${styles.childRow}` : ''}`}
      style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
      onClick={() => onOpen(txn)}
    >
      {/* Column order must match GRID_TEMPLATE_COLUMNS / TableHead:
          TxNumber, TxId, PaymentType, Amount, Date, Carrier, BenefitId,
          CompanyEeId, CompanyName, Insured, Month, Recon, Note, Ticket,
          Action, Log. Column 1 (Transaction Number) carries the child indent. */}
      <span className={`${styles.cell}${isChild ? ` ${styles.indent}` : ''}`}>
        {isChild ? (
          '↳'
        ) : (
          <Highlighted
            text={txn.transactionNumber ?? DASH}
            query={searchQuery}
          />
        )}
      </span>
      <div className={styles.cellStack}>
        {!isChild && (
          <span title={txn.transactionId || undefined}>
            <Highlighted
              text={truncateMiddle(txn.transactionId) || DASH}
              query={searchQuery}
            />
          </span>
        )}
        {!isChild && txn.checkNumber && (
          <span className={styles.subLabel}>
            <Highlighted
              text={`Check no. ${txn.checkNumber}`}
              query={searchQuery}
            />
          </span>
        )}
      </div>
      <span className={styles.cell}>
        {!isChild && (
          <span
            className={`${styles.paymentBadge} ${styles[txn.source] ?? ''}`}
          >
            {sourceLabel(txn.source)}
          </span>
        )}
      </span>
      <span className={`${styles.cell} ${styles.num}`}>{amount}</span>
      <span className={styles.cell}>{formatDate(txn.transactionDate)}</span>
      <span className={styles.cell}>
        <Highlighted text={carrier} query={searchQuery} />
      </span>
      <span
        className={styles.cell}
        title={benefitId === DASH ? undefined : benefitId}
      >
        {benefitId === DASH ? (
          DASH
        ) : allocation ? (
          // Single-allocation rows (plain + child) deep-link to the Benefits
          // tab's Adjust Payment modal for this (benefit, coverage-month).
          // stopPropagation so the row's own detail-modal click doesn't fire.
          <button
            type="button"
            className={styles.benefitLink}
            aria-label={`Adjust payment for benefit ${benefitId}`}
            onClick={(e) => {
              e.stopPropagation()
              onOpenAdjust(allocation, txn.transactionId)
            }}
          >
            <Highlighted text={truncateMiddle(benefitId)} query={searchQuery} />
          </button>
        ) : (
          <Highlighted text={truncateMiddle(benefitId)} query={searchQuery} />
        )}
      </span>
      <span
        className={styles.cell}
        title={companyEeId === DASH ? undefined : companyEeId}
      >
        {companyEeId === DASH ? (
          DASH
        ) : (
          <Highlighted text={truncateMiddle(companyEeId)} query={searchQuery} />
        )}
      </span>
      <span className={styles.cell}>
        {companyName === DASH ? (
          DASH
        ) : (
          <Highlighted text={companyName} query={searchQuery} />
        )}
      </span>
      <span className={styles.cell}>
        {isParent ? (
          <span className={styles.splitBadge}>{insuredName}</span>
        ) : (
          <Highlighted text={insuredName} query={searchQuery} />
        )}
      </span>
      <span className={styles.cell}>{month}</span>
      <span className={`${styles.cell} ${reconClassName}`}>{recon}</span>
      <span
        className={`${styles.cell}${noteText === DASH ? ` ${styles.muted}` : ''}`}
        title={noteText === DASH ? undefined : noteText}
      >
        {noteText === DASH ? (
          DASH
        ) : (
          <Highlighted text={noteText} query={searchQuery} />
        )}
      </span>
      <span className={styles.cell}>
        {tickets.length === 0 ? (
          <span className={styles.muted}>{DASH}</span>
        ) : (
          <button
            type="button"
            className={styles.ticketBtn}
            title={`${tickets.length} ticket${tickets.length > 1 ? 's' : ''} linked`}
            onClick={(e) => {
              e.stopPropagation()
              onOpenTickets(tickets)
            }}
          >
            <TagOutlined />
            {tickets.length > 1 && (
              <span className={styles.ticketCount}>{tickets.length}</span>
            )}
          </button>
        )}
      </span>
      {/* Action: allocate/edit for this row, plus Return on a returnable row. A
          returned row offers only Un-return — allocate/absorb reject it server-side. */}
      <span className={styles.cell}>
        {txn.txReturn ? (
          <button
            type="button"
            className={styles.actionBtn}
            disabled={!canWrite}
            onClick={(e) => {
              e.stopPropagation()
              onUnreturn(txn)
            }}
          >
            Un-return
          </button>
        ) : (
          <span className={styles.actionGroup}>
            <button
              type="button"
              className={styles.actionBtn}
              disabled={!canWrite}
              onClick={(e) => {
                e.stopPropagation()
                onRowAction(row)
              }}
            >
              {rowActionLabel(row)}
            </button>
            {canReturnRow(row) && (
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.returnBtn}`}
                disabled={!canWrite}
                onClick={(e) => {
                  e.stopPropagation()
                  onReturn(row)
                }}
              >
                Return
              </button>
            )}
          </span>
        )}
      </span>
      {/* Log: event-history clock — one per transaction, so only on the tx-level
          row (plain or split parent), not on split-child slices. */}
      <span className={styles.cell}>
        {isChild ? null : (
          <button
            type="button"
            className={styles.ticketBtn}
            title="Event history"
            onClick={(e) => {
              e.stopPropagation()
              onOpenHistory(txn)
            }}
          >
            <ClockCircleOutlined />
          </button>
        )}
      </span>
    </div>
  )
}

export default TransactionRow
